import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const scrollToHash = (hash) => {
  if (typeof document === 'undefined' || !hash.startsWith('#')) {
    return;
  }

  const element = document.querySelector(hash);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const isInternalRoute = (href) => href && href.startsWith('/');

const Nav = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const items = t('navigation.items', { returnObjects: true }) || [];
  const servicesMenu = t('navigation.servicesMenu', { returnObjects: true }) || [];
  const dir = i18n.dir();

  const handleClick = (event, href) => {
    if (href && href.startsWith('#')) {
      event.preventDefault();
      scrollToHash(href);
      if (onNavigate) {
        onNavigate();
      }
    }
  };

  const getChildren = (item) => {
    if (item.id === 'services') {
      return servicesMenu;
    }

    return item.children || [];
  };

  const renderChildLink = (child) => {
    const href = child.href;
    const label = child.label;

    if (!href) {
      return <span>{label}</span>;
    }

    if (href && href.startsWith('#')) {
      return (
        <a
          href={href}
          onClick={(event) => {
            handleClick(event, href);
            if (onNavigate) {
              onNavigate();
            }
          }}
        >
          {label}
        </a>
      );
    }

    if (isInternalRoute(href)) {
      return (
        <Link
          to={href}
          onClick={() => {
            if (onNavigate) {
              onNavigate();
            }
          }}
        >
          {label}
        </Link>
      );
    }

    const isExternal = /^https?:\/\//i.test(href);

    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
        onClick={() => {
          if (onNavigate) {
            onNavigate();
          }
        }}
      >
        {label}
      </a>
    );
  };

  return (
    <nav className="mainmenu-nav">
      <ul className={`mainmenu ${dir === 'rtl' ? 'is-rtl' : ''}`}>
        {items.map((item, index) => {
          const children = getChildren(item);
          const hasChildren = Array.isArray(children) && children.length > 0;

          return (
            <li key={index} className={hasChildren ? 'menu-item-has-children' : ''}>
              <a href={item.href} onClick={(event) => handleClick(event, item.href)}>
                {item.label}
                {hasChildren && <FaChevronDown aria-hidden="true" />}
              </a>
              {hasChildren && (
                <ul className="axil-submenu">
                  {children.map((child, childIndex) => (
                    <li key={childIndex}>{renderChildLink(child)}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

Nav.propTypes = {
  onNavigate: PropTypes.func
};

export default Nav;
